import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../css/paymentSuccess.css";
import { verifyPayment } from "../services/paymentService";

export default function PaymentSuccess() {
  const navigate = useNavigate();

  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  const reference = new URLSearchParams(window.location.search)
    .get("reference");

  useEffect(() => {
    async function verify() {
      if (!reference) {
        setError("No payment reference was found.");
        setLoading(false);
        return;
      }

      try {
        const response = await verifyPayment(reference);
        const verifiedJob = response.data;

        const topupReference =
          sessionStorage.getItem("topupReference");

        const isTopUp =
          topupReference &&
          topupReference === reference;

        /*
         * This was a top-up payment.
         * The backend returns the updated job,
         * including jobId and accessToken.
         */
        if (isTopUp) {
          sessionStorage.removeItem("topupReference");

          window.location.href =
            `/job/${verifiedJob.jobId}?token=${verifiedJob.accessToken}&paid=true`;

          return;
        }

        /*
         * This was the customer's initial payment.
         */
        setJob(verifiedJob);
      } catch (err) {
        setError(
          err.response?.status === 400
            ? "We could not verify this payment."
            : "Something went wrong while verifying your payment."
        );
      } finally {
        setLoading(false);
      }
    }

    verify();
  }, [reference]);

  async function copyTrackingLink() {
    if (!job?.jobId || !job?.accessToken) {
      return;
    }

    const trackingLink =
      `${window.location.origin}/job/${job.jobId}?token=${job.accessToken}`;

    try {
      await navigator.clipboard.writeText(trackingLink);

      setCopied(true);

      setTimeout(() => {
        setCopied(false);
      }, 2000);
    } catch {
      setError("Unable to copy the tracking link.");
    }
  }

  if (loading) {
    return (
      <main className="payment-success-page">
        <div className="payment-success-card">
          <div className="success-icon loading-icon">
            ...
          </div>

          <h1>Verifying your payment</h1>
          <p>
            Please wait while we confirm your payment.
          </p>
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main className="payment-success-page">
        <div className="payment-success-card">
          <div className="success-icon error-icon">
            !
          </div>

          <h1>Payment verification failed</h1>

          <p>{error}</p>

          <button
            className="success-primary-button"
            onClick={() => navigate("/")}
          >
            Back to website
          </button>
        </div>
      </main>
    );
  }

  if (!job) {
    return null;
  }

  const trackingLink =
    `${window.location.origin}/job/${job.jobId}?token=${job.accessToken}`;

  const amountPaid = Number(job.amountPaid || 0);
  const amount = Number(job.amount || 0);
  const balance = Math.max(amount - amountPaid, 0);

  return (
    <main className="payment-success-page">
      <div className="payment-success-card">
        <div className="success-icon">
          ✓
        </div>

        <p className="success-eyebrow">
          Payment confirmed
        </p>

        <h1>Your order is confirmed!</h1>

        <p className="success-description">
          Thank you for your order. Your job has been created
          successfully.
        </p>

        <div className="success-job-info">
          <span>Job ID</span>
          <strong>{job.jobId}</strong>
        </div>

        <div className="success-payment-summary">
          <div>
            <span>Amount paid</span>
            <strong>
              ₦{amountPaid.toLocaleString("en-NG")}
            </strong>
          </div>

          {balance > 0 && (
            <div>
              <span>Balance</span>
              <strong>
                ₦{balance.toLocaleString("en-NG")}
              </strong>
            </div>
          )}
        </div>

        <div className="tracking-section">
          <h2>Track your order</h2>

          <p>
            Save this link to check your order status and
            make any remaining payment later.
          </p>

          <div className="tracking-link-box">
            <input
              type="text"
              value={trackingLink}
              readOnly
              onFocus={(event) => event.target.select()}
            />

            <button onClick={copyTrackingLink}>
              {copied ? "Copied!" : "Copy Link"}
            </button>
          </div>
        </div>

        <button
          className="success-primary-button"
          onClick={() =>
            navigate(
              `/job/${job.jobId}?token=${job.accessToken}`
            )
          }
        >
          Track my order
        </button>
      </div>
    </main>
  );
}